import React, { Component } from 'react';
import { Permissions } from 'permissions';
import { Tenant } from 'models/Blip/Index'
import { PermissionService } from 'services/PermissionService';
import { SubscribedAppsResponse, PhoneNumberInfoResponse } from 'models/response/Index';
import { GridView } from 'models/GridView';
import { Card, Button, List } from 'antd';
import { MODAL_TYPES, ModalFactory } from 'components/ModalFactory';
import { getPhoneNumberInfo, getSubscribedApps, wabaEditApp, changePhoneNumberWebhook } from 'services/CeberusApiService';
import { BaseRequest, EditAppsRequest, EditPhoneNumberWebhookRequest } from 'models/request/Index';
import { TenantService } from 'connection/TenantService';
import { PushNotification } from 'utils/PushNotification';

const ListItem = List.Item
const ListItemMeta = List.Item.Meta

export class WhatsappInformation extends Component<BaseRequest> {
    callbackUriFormRef: any;

    state = {
      isLoading: true,
      editingRecord: {},
      hasModalVisibility: false,
      currentModal: String(),
      applications: new Array<SubscribedAppsResponse>(),
      phoneNumberInfo: new PhoneNumberInfoResponse(),
      gridView: new Array<GridView>(),
      tenantSelected: new Tenant(),
      hasEditPermission: PermissionService.hasPermission(Permissions.Application_Channels_Advanced_Permissions)
    }

    componentDidMount() {
      this.loadPage();
    }

    loadPage = async (): Promise<void> => {
      try {
        const tenantSelected = TenantService.getSelectedTenant();
        this.setState({ isLoading: true, tenantSelected });

        const applications = await getSubscribedApps({ ...this.props });
        this.setState({ applications });

        try{
          const phoneNumberInfo = await getPhoneNumberInfo({ ...this.props });
          this.setState({ phoneNumberInfo });
        }catch(error){
          PushNotification.httpError(error);
        }

        const gridView = this.buildWhatsAppOverview(this.state.applications, this.state.phoneNumberInfo);
        this.setState({ gridView });
      } catch (error) {
        PushNotification.httpError(error);
      } finally {
        this.setState({ isLoading: false });
      }
    }

    handleChangeWabaCallbackUri = () : void => {
      this.setState({ isLoading: true });

      const form = this.callbackUriFormRef.props.form;

      form.validateFields(async (err, values) => {
        if (err) {
          this.setState({ isLoading: false });
          return
        }

        try {
          form.resetFields();
          this.setState({ currentModal: '', editingRecord: {} });

          const { OverrideCallbackUri } = values;

          const request = new EditAppsRequest();
          request.ClusterName = this.state.tenantSelected.name;
          request.OverrideCallbackUri = OverrideCallbackUri;
          request.BotIdentifier = this.props.BotIdentifier;

          const response = await wabaEditApp(request);
          await this.loadPage();

          PushNotification.success(response.Success);
        } catch (error) {
          PushNotification.httpError(error);
        } finally {
          this.setState({ isLoading: false });
        }
      })
    }

    handleChangePhoneNumberCallbackUri = () : void => {
      this.setState({ isLoading: true });

      const form = this.callbackUriFormRef.props.form;

      form.validateFields(async (err, values) => {
        if (err) {
          this.setState({ isLoading: false });
          return
        }

        try {
          form.resetFields();
          this.setState({ currentModal: '', editingRecord: {} });

          const { PhoneNumberWebhookUrl } = values;

          const request = new EditPhoneNumberWebhookRequest();
          request.ClusterName = this.state.tenantSelected.name;
          request.PhoneNumberWebhookUrl = PhoneNumberWebhookUrl;
          request.BotIdentifier = this.props.BotIdentifier;

          const response = await changePhoneNumberWebhook(request);
          await this.loadPage();

          PushNotification.success(response.Success);
        } catch (error) {
          PushNotification.httpError(error);
        } finally {
          this.setState({ isLoading: false });
        }
      })
    }

    handleModalVisibility = (type: string, visibility: boolean) : void => {
      this.setState({ 
        currentModal: visibility ? type : '',
        hasModalVisibility: visibility
      });

      if (!visibility) {
        this.setState({ editingRecord: {} });
      }
    }

    saveCallbackUriFormRef = (formRef: any) => {
      this.callbackUriFormRef = formRef;
    }

    buildWhatsAppOverview = (applications: Array<SubscribedAppsResponse>, phoneNumberInfo: PhoneNumberInfoResponse) : Array<GridView> => {
      let gridView = new Array<GridView>;
      
      gridView.push({
        title: 'Display Phone Number:',
        description: phoneNumberInfo.DisplayPhoneNumber
      });

      gridView.push({
        title: 'Platform Type:',
        description: phoneNumberInfo.PlatformType
      });

      gridView.push({
        title: 'Throughput:',
        description: phoneNumberInfo.Throughput
      });

      gridView.push({
        title: 'Phone Number Webhook:',
        description: [
          phoneNumberInfo.PhoneNumberWebhook, 
          this.getButtonToChangePhoneNumberWebhook(phoneNumberInfo)
        ]
      });

      gridView = applications.reduce((grid: Array<GridView>, subscribedApp: SubscribedAppsResponse, index: number) : Array<GridView> => {
        grid.push({
          title: `App '${subscribedApp.AppName}':`,
          description: subscribedApp.AppId
        });

        grid.push({
          title: `Waba Callback Uri '${subscribedApp.AppName}':`,
          description: [
            subscribedApp.OverrideCallbackUri,
            <Button
              key={`editPlanButton ${subscribedApp.AppId}`}
              type="primary"
              shape="round"
              size="small"
              icon="edit"
              className="ml2"
              disabled={!this.state.hasEditPermission}
              onClick={() => this.handleModalVisibility(MODAL_TYPES.ChangeCallbackUri, true)}>
              Edit
            </Button>
          ]
        });

        return grid;
      }, gridView);

      return gridView;
    }

    getButtonToChangePhoneNumberWebhook(phoneNumberInfo: PhoneNumberInfoResponse) {
      return <Button
        key={`editPlanButton ${phoneNumberInfo.DisplayPhoneNumber}`}
        type="primary"
        shape="round"
        size="small"
        icon="edit"
        className="ml2"
        disabled={!this.state.hasEditPermission}
        onClick={() => this.handleModalVisibility(MODAL_TYPES.ChangePhoneNumberWebhookUri, true)}>
        { phoneNumberInfo.PhoneNumberWebhook ? 'Edit' : 'Enable phoneNumber Webhook' }
      </Button>;
    }

    render() {
      return (<div>
        <ModalFactory
          wrappedComponentRef={this.saveCallbackUriFormRef}
          organization={'this.state.overviewResponse.Organization'}
          chatbot={'this.state.overviewResponse.Identity'}
          visible={this.state.hasModalVisibility}
          onCancel={() => this.handleModalVisibility(MODAL_TYPES.ChangeCallbackUri, false)}
          onOk={this.state.currentModal == MODAL_TYPES.ChangeCallbackUri ? this.handleChangeWabaCallbackUri : this.handleChangePhoneNumberCallbackUri}
          loading={this.state.isLoading}
          type={this.state.currentModal}
          record={this.state.editingRecord}
          maskClosable={false}
        />
        <Card title='Whatsapp Information' className="mb3" loading={this.state.isLoading}>
          <List
            loading={this.state.isLoading}
            itemLayout="horizontal"
            dataSource={this.state.gridView}
            renderItem={item =>
              !item.invisible ? (
                <ListItem>
                  <ListItemMeta
                    title={item.title}
                    description={item.description}>
                  </ListItemMeta>
                </ListItem>
              ) : (
                <></>
              )}></List>
        </Card>
      </div>);
    }
}
